import { buildMenuTree, getAllDescendants } from "./menuUtils";
import { formatMenuLabel, getTopLevelMenus, isTmsModule, pathMatchesMenu } from "./layoutTheme";
import { resolveMenuNavUrl } from "./tmsRouteUtils";
import { applyTmsMenuIcons, resolveTmsMenuIcon } from "./tmsSidebarMenu";

export type NavSearchItem = {
    id: string;
    label: string;
    breadcrumb: string[];
    url?: string;
    moduleCode: string;
    moduleName: string;
    icon: string;
};

function findFirstUrl(node: any): string | undefined {
    const resolved = resolveMenuNavUrl(node);
    if (resolved) return resolved;
    for (const child of node.children || []) {
        const found = findFirstUrl(child);
        if (found) return found;
    }
    return undefined;
}

function flattenNodes(
    nodes: any[],
    ctx: { moduleCode: string; moduleName: string; ancestors: string[] }
): NavSearchItem[] {
    const results: NavSearchItem[] = [];

    for (const node of nodes || []) {
        const label = formatMenuLabel(node.moduleName, node.code);
        if (!label) continue;

        const breadcrumb = [...ctx.ancestors, label];
        const resolved = resolveMenuNavUrl(node);
        const url = node.children?.length ? findFirstUrl(node) ?? resolved : resolved ?? findFirstUrl(node);

        results.push({
            id: `${ctx.moduleCode}-${node.id ?? node.code}`,
            label,
            breadcrumb,
            url,
            moduleCode: ctx.moduleCode,
            moduleName: ctx.moduleName,
            icon: resolveTmsMenuIcon(node),
        });

        if (node.children?.length) {
            results.push(
                ...flattenNodes(node.children, {
                    ...ctx,
                    ancestors: breadcrumb,
                })
            );
        }
    }

    return results;
}

function buildTmsSearchItems(tree: any[], moduleCode: string, moduleName: string): NavSearchItem[] {
    const nodes = applyTmsMenuIcons(tree);
    return flattenNodes(nodes, { moduleCode, moduleName, ancestors: [moduleName] });
}

function buildCmsSearchItems(tree: any[], moduleCode: string, moduleName: string): NavSearchItem[] {
    return flattenNodes(tree, { moduleCode, moduleName, ancestors: [moduleName] });
}

/** Builds a flat, searchable list of all sidebar menus and sub-menus the user can access. */
export function buildNavSearchItems(modulePermission: any[] | undefined): NavSearchItem[] {
    const menuList = (modulePermission || []).filter((f) => f?.isIncludeMenu === true);
    const topMenus = getTopLevelMenus(modulePermission);
    const results: NavSearchItem[] = [];

    for (const top of topMenus) {
        const descendants = getAllDescendants(menuList, top.code);
        const tree = buildMenuTree(descendants);
        const moduleName = top.moduleName || top.code || "";

        if (isTmsModule(top)) {
            results.push(...buildTmsSearchItems(tree, top.code, moduleName));
        } else {
            results.push(...buildCmsSearchItems(tree, top.code, moduleName));
        }
    }

    return results;
}

/** Resolves the top header module (CMS/TMS) that owns the current route. */
export function findTopModuleCodeForPath(
    modulePermission: any[] | undefined,
    pathname: string,
    search = ""
): string | undefined {
    const menuList = (modulePermission || []).filter((f) => f?.isIncludeMenu === true);
    const topMenus = getTopLevelMenus(modulePermission);

    for (const top of topMenus) {
        const descendants = getAllDescendants(menuList, top.code);
        const matches = descendants.some((m) =>
            pathMatchesMenu(pathname, resolveMenuNavUrl(m), search)
        );
        if (matches) return top.code;
    }

    return undefined;
}

/** First navigable URL for a top-level module (used when switching header tabs). */
export function getFirstMenuUrlForModule(
    modulePermission: any[] | undefined,
    topModuleCode: string
): string | undefined {
    const menuList = (modulePermission || []).filter((f) => f?.isIncludeMenu === true);
    const descendants = getAllDescendants(menuList, topModuleCode);
    const tree = buildMenuTree(descendants);
    const top = getTopLevelMenus(modulePermission).find((m) => m.code === topModuleCode);
    const nodes = isTmsModule(top) ? applyTmsMenuIcons(tree) : tree;

    if (!nodes?.length) return undefined;

    const firstUrl = resolveMenuNavUrl(nodes[0]);
    if (firstUrl) return firstUrl;

    const child = nodes[0]?.children;
    return child?.length ? resolveMenuNavUrl(child[0]) : undefined;
}

export function filterNavSearchItems(items: NavSearchItem[], query: string, limit = 12): NavSearchItem[] {
    const q = query.trim().toLowerCase();
    if (!q) return [];

    return items
        .filter((item) => {
            const haystack = [item.label, ...item.breadcrumb, item.moduleName].join(" ").toLowerCase();
            return haystack.includes(q);
        })
        .slice(0, limit);
}
